"use client";

import ProductDetailsModal from "@/components/common/product-details-modal/product-details-modal";
import ProductIdeaModal from "@/components/common/product-idea";
import GrowWithUsModal from "@/modules/grow-with-us/components/grow-with-us-modal";
import { createContext, useCallback, useContext, useEffect, useState } from "react";

import AuthModal from "@/components/new/common/auth-modal";
import { BASE_URL } from "@/lib/constants";
import UpdateProfileModal from "@/modules/update-profile/components/update-profile-modal";
import { TGetAccountUpgradeInfoResponseDTO } from "@/types/dto/account";
import { useQuery } from "@tanstack/react-query";
import { usePathname } from "next/navigation";
import { useAuth } from "./auth-provider";
import CreatePostModal from "@/modules/community/post/components/create-post/create-post-modal";
import ContactUsModal from "@/modules/contact-us/contact-us-modal";
import { TProduct } from "@/types/product";
import { getProductInfo } from "@/services/products";

interface ModalContextProps {
	productSlug: string;
	openProductDetailsModal: (slug: string) => void;
	closeProductDetailsModal: (disableURLChange?: boolean) => void;
	openAuthModal: (onAuthSuccess?: () => void) => void;
	closeAuthModal: () => void;
	openProductIdeaModal: () => void;
	closeProductIdeaModal: () => void;
	openGrowWithUsModal: (message?: string) => void;
	closeGrowWithUsModal: () => void;
	openContactUsModal: () => void;
	closeContactUsModal: () => void;
	openUpdateProfileModal: () => void;
	closeUpdateProfileModal: () => void;
	openVIPUpgradeModal: () => void;
	closeVIPUpgradeModal: () => void;
	openCreatePostModal: () => void;
}

const ModalContext = createContext<ModalContextProps | undefined>(undefined);

const fetchUserUpgradeInfo = async () => {
	try {
		const response = await fetch(`${BASE_URL}/api/account/upgrade`);
		if (!response.ok) {
			return {
				isUpgradedToVIP: false,
				upgradedAt: null,
			};
		}

		const { data } = await response.json();

		return data;
	} catch (error) {
		console.log(error);

		return {
			isUpgradedToVIP: false,
			upgradedAt: null,
		};
	}
};

const getProductDetails = async (productSlug: string): Promise<TProduct> => {
	return await getProductInfo(productSlug);
};

export const ModalProvider = ({ children }: { children: React.ReactNode }) => {
	const pathname = usePathname();

	const [productSlug, setProductSlug] = useState<string>("");
	const [isProductDetailsModalOpen, setIsProductDetailsModalOpen] = useState<boolean>(false);
	const [isAuthModalOpen, setIsAuthModalOpen] = useState<boolean>(false);
	const [isProductIdeaModalOpen, setIsProductIdeaModalOpen] = useState<boolean>(false);
	const [isGrowWithUsModalOpen, setIsGrowWithUsModalOpen] = useState<boolean>(false);
	const [isContactUsModalOpen, setIsContactUsModalOpen] = useState<boolean>(false);
	const [isUpdateProfileModalOpen, setIsUpdateProfileModalOpen] = useState<boolean>(false);
	const [growWithUsMessage, setGrowWithUsMessage] = useState<string>("");
	const [isVIPUpgradeModalOpen, setIsVIPUpgradeModalOpen] = useState<boolean>(false);
	const [authSuccessCallback, setAuthSuccessCallback] = useState<(() => void) | undefined>();
	const [isCreatePostModalOpen, setIsCreatePostModalOpen] = useState(false);

	const { isLoggedIn } = useAuth();

	const { data: userUpgradeInfo } = useQuery<TGetAccountUpgradeInfoResponseDTO>({
		queryKey: ["userUpgradeInfo"],
		queryFn: fetchUserUpgradeInfo,
		enabled: !!isLoggedIn,
		refetchOnWindowFocus: false,
	});

	const { data: productDetails } = useQuery<TProduct>({
		queryKey: ["productDetails", productSlug],
		queryFn: () => getProductDetails(productSlug),
		enabled: !!isProductDetailsModalOpen && !!productSlug,
		refetchOnWindowFocus: false,
	});

	const openCreatePostModal = () => {
		setIsCreatePostModalOpen(true);
	};
	const openProductDetailsModal = (slug: string) => {
		setIsProductDetailsModalOpen(true);
		setProductSlug(slug);
	};

	const closeProductDetailsModal = (disableURLChange?: boolean) => {
		if (!disableURLChange) {
			history.pushState({}, "", pathname);
		}

		setIsProductDetailsModalOpen(false);
	};

	const openAuthModal = (onAuthSuccess?: () => void) => {
		setAuthSuccessCallback(() => onAuthSuccess);
		setIsAuthModalOpen(true);
	};

	const closeAuthModal = () => {
		setIsAuthModalOpen(false);
		setAuthSuccessCallback(undefined);
	};

	const openProductIdeaModal = () => {
		setIsProductIdeaModalOpen(true);
	};
	const closeProductIdeaModal = () => {
		setIsProductIdeaModalOpen(false);
	};

	const openGrowWithUsModal = (message?: string) => {
		if (message) {
			setGrowWithUsMessage;
		}

		setIsGrowWithUsModalOpen(true);
	};

	const closeGrowWithUsModal = () => {
		setIsGrowWithUsModalOpen(false);
	};

	const closeContactUsModal = () => {
		setIsContactUsModalOpen(false);
	};

	const openUpdateProfileModal = () => {
		setIsUpdateProfileModalOpen(true);
	};

	const openContactUsModal = () => {
		setIsContactUsModalOpen(true);
	};

	const closeUpdateProfileModal = () => {
		setIsUpdateProfileModalOpen(false);
	};

	const openVIPUpgradeModal = useCallback(() => {
		setIsVIPUpgradeModalOpen(true);
	}, []);

	const closeVIPUpgradeModal = () => {
		// TODO: Remove this line
		console.log(isVIPUpgradeModalOpen);

		setIsVIPUpgradeModalOpen(false);
	};

	useEffect(() => {
		if (userUpgradeInfo?.isUpgradedToVIP) {
			openVIPUpgradeModal();
		}
	}, [userUpgradeInfo, openVIPUpgradeModal]);

	return (
		<ModalContext.Provider
			value={{
				productSlug,
				openContactUsModal,
				closeContactUsModal,
				openProductDetailsModal,
				closeProductDetailsModal,
				openAuthModal,
				closeAuthModal,
				openProductIdeaModal,
				closeProductIdeaModal,
				openGrowWithUsModal,
				closeGrowWithUsModal,
				openUpdateProfileModal,
				closeUpdateProfileModal,
				openVIPUpgradeModal,
				closeVIPUpgradeModal,
				openCreatePostModal,
			}}
		>
			{children}

			<AuthModal
				isOpen={isAuthModalOpen}
				onClose={() => setIsAuthModalOpen(false)}
				onAuthSuccess={authSuccessCallback}
			/>

			<ProductDetailsModal
				isOpen={isProductDetailsModalOpen}
				onClose={closeProductDetailsModal}
				slug={productSlug}
				product={productDetails as TProduct}
			/>

			<ProductIdeaModal isOpen={isProductIdeaModalOpen} onClose={closeProductIdeaModal} />

			<GrowWithUsModal
				isOpen={isGrowWithUsModalOpen}
				onClose={closeGrowWithUsModal}
				message={growWithUsMessage}
			/>

			<ContactUsModal isOpen={isContactUsModalOpen} onClose={closeContactUsModal} />

			<CreatePostModal open={isCreatePostModalOpen} setOpen={setIsCreatePostModalOpen} />
			<UpdateProfileModal isOpen={isUpdateProfileModalOpen} onClose={closeUpdateProfileModal} />
		</ModalContext.Provider>
	);
};

export const useModal = () => {
	const context = useContext(ModalContext);
	if (context === undefined) {
		throw new Error("useModal must be used within a ModalProvider");
	}

	return context;
};
